import { BasicButton } from '../../../../../../widgets/buttons';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { NotificationBanner } from '../../../../../../widgets/notificationBanner';
import { TextArea } from '../../../../../../widgets/textArea';
import { ZSwitch } from '../../../../../../widgets/zswitch/zswitch';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import { FC } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T } from 'src/shared/helpers';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import styled from 'styled-components';
import Composer3DPreviewCapture from '../../../configurator-composer/composer/components/composer3DPreviewCapture';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import ComposerValidationTooltip from '../../../configurator-composer/composer/components/composerValidationTooltip';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerRevealButton } from '../composerReveal';
import { createComposerLocalCode, isComposerStoreCode } from '../../../configurator-composer/composer/helpers/composerLocalCode';
import { applyComposerOptionImage, createComposerOptionImageReference } from '../../../configurator-composer/composer/helpers/composerOptionImage';
import {
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerKindBadge,
    ComposerPanel,
    ComposerSection,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributePreviewType } from './composerAttributesModels';
import ComposerLinksEditor from './composerLinksEditor';
import ComposerRetargetPanel from './composerRetargetPanel';
import { ComposerCameraSlot, ComposerChoice, ComposerObjectSlots, ComposerSlot } from '../composerSlots';
import { ComposerPresetBox, ComposerPresetButtons } from '../composerWorkspace';
import StoreIcon from 'src/shared/material_icons/action/store.svg';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';

// L'INSPECTOR DELL'ATTRIBUTO: Settings e Links. Le opzioni stanno nell'albero (round 4, pacchetto H,
// `composerOptionRows.tsx`); qui resta il comando che ne aggiunge una.
const ComposerOptions: FC<{
    selectedAttribute: ComposerAttributeNode | null;
    options: ComposerAttributeOptionNode[];
    multiple: boolean;
}> = ({ selectedAttribute, options, multiple }) => {
    const [
        attributes,
        sceneMaterialList,
        sceneMeshList,
        updateAttribute,
        updateOption,
        selectOptions,
        validateAttributes,
        validateOptions,
        attributeFace,
        setAttributeFace,
        merchantProductOptions,
        merchantProductOptionsError
    ] = useComposerStore((store) => [
        store.attributes,
        store.sceneMaterialList,
        store.sceneObjectList,
        store.updateAttribute,
        store.updateOption,
        store.selectOptions,
        store.validateAttributes,
        store.validateOptions,
        store.attributeFace,
        store.setAttributeFace,
        store.merchantProductOptions,
        store.merchantProductOptionsError
    ]);
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);

    // UN ATTRIBUTO VUOTO PROPONE DA DOVE PARTIRE (round 4, pacchetto I, dal prototipo): tre opzioni già
    // nominate in un clic. Nascono senza azioni, come quelle di «Add option»: che cosa fanno lo si decide dopo.
    const addPreset = (names: string[]) => {
        if (!selectedAttribute) return;
        names.forEach((name, displayOrder) => {
            const option = ComposerAttributeNodeAdapter.toNodeOption(selectedAttribute.id, ComposerAttributeNodeAdapter.createNewOption(displayOrder));
            updateOption({ ...option, name, description: '', attributeID: selectedAttribute.id, displayOrder, isDefault: displayOrder === 0, isNew: true });
        });
        void validateAttributes();
    };
    const materialNames = sceneMaterialList
        .map((material) => material.name)
        .filter((name) => name && name !== 'default material')
        .slice(0, 3);

    if (!selectedAttribute || multiple) {
        return (
            <ComposerPanel>
                <ComposerEmptyState icon={<AttributeIcon />}>{T._('Select one attribute to see its options and settings.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const activeTab = attributeFace === 'links' ? 1 : attributeFace === 'retarget' ? 2 : 0;
    const updateSelectedAttribute = (changes: Partial<ComposerAttributeNode>) => {
        updateAttribute({ ...selectedAttribute, ...changes, isNew: true });
        if (changes.name !== undefined || changes.code !== undefined || Object.prototype.hasOwnProperty.call(changes, 'platformAttribute')) {
            void validateAttributes();
        }
    };
    const optionsWithImage = selectedAttribute.options.filter((option) => !!option.img).length;
    const commonOptionImage = selectedAttribute.options[0]?.img;
    const hasCommonOptionImage = !!commonOptionImage && selectedAttribute.options.every((option) => option.img === commonOptionImage);
    const updateAttributeOptionImages = async (uploadedImageUrl: string, tempImageFileKey?: string) => {
        const image = createComposerOptionImageReference(uploadedImageUrl, tempImageFileKey);
        const updatedOptions = selectedAttribute.options.map((option) => applyComposerOptionImage(option, image));
        updatedOptions.forEach((option) => updateOption(option));
        await validateOptions({ ...selectedAttribute, options: updatedOptions });
        return image?.imageUrl ?? '';
    };
    const platformAttributes = [
        { value: '', label: T._('Not connected', 'Admin') },
        ...merchantProductOptions.map((attribute) => ({
            value: attribute.optionID,
            label: attribute.label || attribute.optionID
        }))
    ];
    const changePlatformAttribute = (selection: { value: string; label: string } | null) => {
        const platformAttribute = selection?.value ? { id: selection.value, label: selection.label } : undefined;
        const storeAttribute = platformAttribute ? merchantProductOptions.find((attribute) => attribute.optionID === platformAttribute.id) : undefined;
        const wasStoreAttribute = Boolean(selectedAttribute.platformAttribute?.id) || isComposerStoreCode(selectedAttribute.code);

        const updatedOptions = platformAttribute
            ? (storeAttribute?.values ?? []).map((storeOption, displayOrder) => {
                  const existingOption = selectedAttribute.options.find((option) => option.platformAttribute?.id === storeOption.valueID);
                  const option =
                      existingOption ??
                      ComposerAttributeNodeAdapter.toNodeOption(selectedAttribute.id, ComposerAttributeNodeAdapter.createNewOption(displayOrder));
                  return {
                      ...option,
                      attributeID: selectedAttribute.id,
                      name: storeOption.label || storeOption.valueID,
                      code: JSON.stringify({ id: storeOption.valueID, label: storeOption.label || storeOption.valueID, selected: true }),
                      displayOrder,
                      isDefault: existingOption?.isDefault ?? displayOrder === 0,
                      isNew: existingOption?.isNew ?? true,
                      platformAttribute: { id: storeOption.valueID, label: storeOption.label || storeOption.valueID }
                  };
              })
            : selectedAttribute.options
                  .filter((option) => !option.platformAttribute?.id && !isComposerStoreCode(option.code))
                  .map((option, displayOrder) => ({ ...option, displayOrder }));

        const defaultOption = updatedOptions.find((option) => option.isDefault) ?? updatedOptions[0];
        const normalizedOptions = updatedOptions.map((option) => ({ ...option, isDefault: option.id === defaultOption?.id }));
        const retainedOptionIds = new Set(normalizedOptions.map((option) => option.id));

        selectOptions(defaultOption ? [normalizedOptions.find((option) => option.id === defaultOption.id)!] : []);
        updateSelectedAttribute({
            platformAttribute,
            options: normalizedOptions,
            pricings: selectedAttribute.pricings.filter((pricing) => retainedOptionIds.has(pricing.selectedOptionGuid)),
            code: platformAttribute
                ? JSON.stringify({ ...platformAttribute, selected: true })
                : wasStoreAttribute
                  ? createComposerLocalCode(selectedAttribute.name, 'attribute')
                  : selectedAttribute.code
        });
    };
    const cameraOptions =
        zkScene
            ?.get?.('cameraLocations')
            ?.models?.map((location) => ({ value: location.getLocationID(), label: location.get('name') || location.getLocationID() })) ?? [];
    const meshOptions = sceneMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh)).map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const selectedMeshes = (selectedAttribute.objects || '').split(/[;,]/).filter(Boolean);
    const attributeNameHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_NOT_UNIQUE
    );
    const attributeCodeHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const attributeStoreHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.BIGCOMMERCE_ATTRIBUTE_NOT_CONNECTED,
        ComposerValidationErrorCode.STORE_ATTRIBUTE_NOT_FOUND,
        ComposerValidationErrorCode.STORE_ATTRIBUTE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );

    return (
        <ComposerPanel aria-label={T._('Attribute options', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='attribute'>
                    <AttributeIcon />
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Attribute name', 'Admin')}
                        placeholder={T._('Enter attribute name', 'Admin')}
                        isError={attributeNameHasError}
                        value={selectedAttribute.name}
                        onChange={(event) => updateSelectedAttribute({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='attribute'>{T._('Attribute', 'Admin')}</ComposerKindBadge>
                        <span>{T._(`${options.length} options`, 'Admin')}</span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                <ComposerRevealButton id={selectedAttribute.id} />
            </ComposerSubjectHeader>
            {/* «+ Add option» NON STA PIÙ QUI (utente, 2026-10-15): un'opzione nasce dal «+» della riga
                dell'attributo nell'albero (addComposerOption). Resta la partenza da un preset. */}
            {options.length === 0 && (
                <ComposerPresetBox>
                    <span>{T._('This attribute offers no options yet. Start from:', 'Admin')}</span>
                    <ComposerPresetButtons>
                        <BasicButton $size='small' onClick={() => addPreset([T._('White', 'Admin'), T._('Red', 'Admin'), T._('Blue', 'Admin')])}>
                            {T._('Colors', 'Admin')}
                        </BasicButton>
                        <BasicButton $size='small' onClick={() => addPreset(['S', 'M', 'L'])}>
                            {T._('Sizes', 'Admin')}
                        </BasicButton>
                        <BasicButton $size='small' disabled={materialNames.length === 0} onClick={() => addPreset(materialNames)}>
                            {T._('Materials', 'Admin')}
                        </BasicButton>
                    </ComposerPresetButtons>
                </ComposerPresetBox>
            )}
            <ComposerTabs selectedIndex={activeTab} onSelect={(index) => setAttributeFace(index === 1 ? 'links' : index === 2 ? 'retarget' : 'settings')}>
                <TabList hasOverflow>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedAttribute.links.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Retarget', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerSection id='attribute.general' forceOpen={attributeCodeHasError || attributeStoreHasError} title={T._('General', 'Admin')}>
                                <FormControl
                                    label={
                                        <>
                                            {T._('Store attribute (optional)', 'Admin')}
                                            {merchantProductOptionsError && <ComposerValidationTooltip messages={[merchantProductOptionsError]} />}
                                        </>
                                    }
                                >
                                    <ComposerSlot
                                        id={`composer-attribute-${selectedAttribute.id}-store`}
                                        value={selectedAttribute.platformAttribute?.id || undefined}
                                        options={platformAttributes.filter((attribute) => attribute.value)}
                                        glyph={<StoreIcon />}
                                        caption={T._('Store attribute', 'Admin')}
                                        emptyLabel={T._('Not connected', 'Admin')}
                                        emptyCaption={T._('Choose a store attribute', 'Admin')}
                                        searchPlaceholder={T._('Search store attributes…', 'Admin')}
                                        noOptionsLabel={T._('No store attributes are available for this product. Create them in your store first.', 'Admin')}
                                        missingLabel={selectedAttribute.platformAttribute?.label}
                                        isError={attributeStoreHasError}
                                        disabled={Boolean(merchantProductOptionsError)}
                                        clearable
                                        onChange={(value) =>
                                            changePlatformAttribute(platformAttributes.find((attribute) => attribute.value === value && value) ?? null)
                                        }
                                    />
                                </FormControl>
                                <FormControl label={T._('Code', 'Admin')}>
                                    {selectedAttribute.platformAttribute?.id ? (
                                        <ComposerDetailValue>
                                            {T._('Managed by the connected store attribute. Disconnect it to edit a local code.', 'Admin')}
                                        </ComposerDetailValue>
                                    ) : (
                                        <Input
                                            placeholder={T._('Enter attribute code', 'Admin')}
                                            isError={attributeCodeHasError}
                                            value={selectedAttribute.code}
                                            onChange={(event) => updateSelectedAttribute({ code: event.target.value })}
                                        />
                                    )}
                                </FormControl>
                                <FormControl label={T._('Description', 'Admin')}>
                                    <TextArea
                                        rows={4}
                                        placeholder={T._('Enter attribute description', 'Admin')}
                                        value={selectedAttribute.description}
                                        onChange={(event) => updateSelectedAttribute({ description: event.target.value })}
                                    />
                                </FormControl>
                            </ComposerSection>
                            {/* SONO LE IMMAGINI DELLE OPZIONI, NON DELL'ATTRIBUTO (utente, 2026-10-16). Un attributo un'immagine sua
                                non ce l'ha — né campo né salvataggio, `MPlaza.Attribute` —, e questa sezione ne mette una uguale su
                                tutte le sue opzioni. Col titolo «Preview images» sembrava l'immagine dell'attributo, e scoprire che
                                aveva sostituito quelle delle opzioni era una sorpresa. */}
                            <ComposerSection id='attribute.preview-images' title={T._('Option images', 'Admin')}>
                                {selectedAttribute.options.length > 0 && (
                                    <FormControl
                                        label={T._('Upload one image for all options', 'Admin')}
                                        caption={T._('The uploaded image will replace the preview image of every option in this attribute.', 'Admin')}
                                    >
                                        <ComposerUploadComponent
                                            passedImgUrl={hasCommonOptionImage ? commonOptionImage : undefined}
                                            onImagePick={updateAttributeOptionImages}
                                        />
                                    </FormControl>
                                )}
                                {optionsWithImage > 0 && (
                                    <NotificationBanner
                                        type='WARNING'
                                        variant='slim'
                                        content={
                                            optionsWithImage === 1
                                                ? T._('1 option already has a preview image. It will be replaced by the new one.', 'Admin')
                                                : T._(
                                                      `${optionsWithImage} options already have a preview image. They will be replaced by the new one.`,
                                                      'Admin'
                                                  )
                                        }
                                    />
                                )}
                                <Composer3DPreviewCapture
                                    materials={sceneMaterialList}
                                    disabled={selectedAttribute.options.length === 0}
                                    helperText={T._(
                                        'Create one image from the 3D scene and apply it to all the options of this attribute. Use object captures the object as it is shown now, Use material captures the selected material.',
                                        'Admin'
                                    )}
                                    onImagePick={updateAttributeOptionImages}
                                    successMessage={T._('The 3D preview was assigned to all options of this attribute.', 'Admin')}
                                />
                                {selectedAttribute.options.length === 0 && (
                                    <ComposerUnsupportedNote>
                                        {T._('Add at least one option before assigning its preview image.', 'Admin')}
                                    </ComposerUnsupportedNote>
                                )}
                            </ComposerSection>
                            <ComposerSection id='attribute.display' title={T._('Display', 'Admin')}>
                                <FormControl label={T._('Preview options', 'Admin')}>
                                    <ComposerChoice
                                        ariaLabel={T._('Preview options', 'Admin')}
                                        options={[
                                            { value: ComposerAttributePreviewType.Rectangle, label: T._('Square', 'Admin') },
                                            { value: ComposerAttributePreviewType.Circle, label: T._('Round', 'Admin') }
                                        ]}
                                        value={
                                            selectedAttribute.previewType === ComposerAttributePreviewType.Circle
                                                ? ComposerAttributePreviewType.Circle
                                                : ComposerAttributePreviewType.Rectangle
                                        }
                                        onChange={(previewType) => updateSelectedAttribute({ previewType })}
                                    />
                                </FormControl>
                                <FormControl label={T._('Hide options label', 'Admin')}>
                                    <ZSwitch
                                        ariaLabel={T._('Hide options label', 'Admin')}
                                        checked={!!selectedAttribute.hideOptionsLabel}
                                        onChange={(checked) => updateSelectedAttribute({ hideOptionsLabel: checked })}
                                    />
                                </FormControl>
                            </ComposerSection>
                            <ComposerSection id='attribute.camera' title={T._('Camera', 'Admin')}>
                                <FormControl
                                    label={T._('Camera position', 'Admin')}
                                    tooltipLabelHtml={T._(
                                        'The camera moves to this position when the customer selects this attribute in the configurator.',
                                        'Admin'
                                    )}
                                >
                                    <ComposerCameraSlot
                                        id={`composer-attribute-${selectedAttribute.id}-camera`}
                                        value={selectedAttribute.camera}
                                        options={cameraOptions}
                                        onChange={(camera) => updateSelectedAttribute({ camera })}
                                    />
                                </FormControl>
                                <FormControl
                                    label={T._('Objects', 'Admin')}
                                    tooltipLabelHtml={T._('When you click on these objects the camera will move to the selected position', 'Admin')}
                                >
                                    <ComposerObjectSlots
                                        id={`composer-attribute-${selectedAttribute.id}-objects`}
                                        value={selectedMeshes}
                                        options={meshOptions}
                                        onChange={(ids) => updateSelectedAttribute({ objects: ids.join(';') })}
                                    />
                                </FormControl>
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerLinksEditor
                        attributes={attributes}
                        links={selectedAttribute.links}
                        logicalOperator={selectedAttribute.linkOperation}
                        selfAttributeID={selectedAttribute.id}
                        subjectLabel={T._('This attribute', 'Admin')}
                        validationErrors={selectedAttribute.brokenValues}
                        onChange={(links, linkOperation) => {
                            updateSelectedAttribute({ links, linkOperation });
                            validateAttributes();
                        }}
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerRetargetPanel
                        actions={selectedAttribute.options.flatMap((option) => option.actions)}
                        objects={sceneMeshList}
                        materials={sceneMaterialList}
                        subjectLabel={selectedAttribute.name}
                        onApply={(remapped) =>
                            selectedAttribute.options.forEach((option) =>
                                updateOption({
                                    ...option,
                                    actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                                })
                            )
                        }
                    />
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default ComposerOptions;
